import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, Transaction } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { EditorState } from 'prosemirror-state';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        invisibleCharacters: {
            showInvisibleCharacters: () => ReturnType;
            hideInvisibleCharacters: () => ReturnType;
            toggleInvisibleCharacters: () => ReturnType;
        };
    }
}

const InvisibleCharactersKey = new PluginKey<{ visible: boolean }>('InvisibleCharacters');

function createInvisibleCharacterDecorations(doc: EditorState['doc']): DecorationSet {
    const decorations: Decoration[] = [];
    let prevFontSize = '12pt';

    doc.descendants((node, pos) => {
        if (node.type.name === 'hardBreak') {
            const fontSize = node.marks.find(mark => mark.type.name === 'textStyle')?.attrs.fontSize || prevFontSize;
            const brWidget = Decoration.widget(
                pos,
                () => {
                    const span = document.createElement('span');
                    span.style.color = '#6aa6ff';
                    span.style.userSelect = 'none';
                    span.style.pointerEvents = 'none';
                    span.style.padding = '0';
                    span.style.whiteSpace = 'normal';
                    span.style.fontSize = fontSize;
                    span.classList.add('invisible-character');
                    span.classList.add(`before:content-['↓']`);
                    span.classList.add(`before:inline-block`);
                    span.classList.add(`before:w-0`);
                    return span;
                },
                { side: 0 },
            );

            decorations.push(brWidget);
        }

        if (node.type.name === 'paragraph') {
            const fontSize = node.attrs?.paragraphBreakFont?.fontSize || prevFontSize;
            prevFontSize = fontSize;
            const endPos = pos + node.nodeSize - 1;
            const eopWidget = Decoration.widget(
                endPos,
                () => {
                    const span = document.createElement('span');
                    span.style.color = '#6aa6ff';
                    span.style.userSelect = 'none';
                    span.style.pointerEvents = 'none';
                    span.style.padding = '0';
                    span.style.whiteSpace = 'normal';
                    span.style.fontSize = fontSize;
                    span.classList.add('invisible-character');
                    span.classList.add(`before:content-['↵']`);
                    span.classList.add(`before:inline-block`);
                    span.classList.add(`before:w-0`);
                    return span;
                },
                { side: 0 },
            );

            decorations.push(eopWidget);
        }
    });

    return DecorationSet.create(doc, decorations);
}

export const InvisibleCharacters = Extension.create({
    name: 'invisibleCharacters',

    addOptions() {
        return {
            visible: false, // 기본적으로 숨김
        };
    },

    addStorage() {
        return {
            visible: this.options.visible,
        };
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: InvisibleCharactersKey,
                state: {
                    init: (_, { doc }) => (this.storage.visible ? createInvisibleCharacterDecorations(doc) : DecorationSet.empty),
                    apply: (tr: Transaction, oldDecorations, oldState, newState) => {
                        return this.storage.visible ? createInvisibleCharacterDecorations(newState.doc) : DecorationSet.empty;
                    },
                },
                props: {
                    decorations: state => {
                        const pluginState = InvisibleCharactersKey.getState(state);
                        return this.storage.visible ? (pluginState instanceof DecorationSet ? pluginState : DecorationSet.empty) : DecorationSet.empty;
                    },
                },
            }),
        ];
    },

    addCommands() {
        return {
            showInvisibleCharacters:
                () =>
                ({ tr, dispatch }) => {
                    this.storage.visible = true;
                    dispatch?.(tr);
                    return true;
                },

            hideInvisibleCharacters:
                () =>
                ({ tr, dispatch }) => {
                    this.storage.visible = false;
                    dispatch?.(tr);
                    return true;
                },

            toggleInvisibleCharacters:
                () =>
                ({ tr, dispatch }) => {
                    this.storage.visible = !this.storage.visible;
                    dispatch?.(tr);
                    return true;
                },
        };
    },
});
